import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api, APIError } from "@/lib/api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";

// AI OAuth consent page. mcp-service's /oauth/authorize 302-redirects
// the browser here (?flow=…). Because this route lives under _app, an
// unauthenticated hit is bounced through /login?redirect=… and returns
// here after the admin signs in — no special handling needed.
//
// Before an admin may grant access, a step-up MFA re-proves possession of
// a second factor (skipped automatically for admins with no factor).

interface ConsentSearch {
  flow?: string;
}

interface FlowView {
  flow_id: string;
  client_id: string;
  client_name: string;
  redirect_host: string;
  scopes: string[];
  status: string;
}

interface StepUpFactor {
  factor_id: string;
  kind: string;
  friendly_name: string;
}

interface StepUpStart {
  mfa_required: boolean;
  challenge_id?: string;
  factors?: StepUpFactor[];
}

const SCOPE_LABELS: Record<string, string> = {
  "catalog:read": "Read the catalog (categories, events, markets)",
  "catalog:write": "Create & edit catalog entries — as drafts only",
  "data:read": "Read trading data (orders, positions, wallets, order books)",
  "data:pii": "Read unredacted user PII (email, phone, KYC)",
  "ops:read": "Read reconciliation / ops findings",
};

const FACTOR_KIND_LABEL: Record<string, string> = {
  TOTP: "Authenticator app",
  EMAIL_OTP: "Email code",
  BACKUP_CODE: "Backup code",
  WEBAUTHN: "Security key",
};

export const Route = createFileRoute("/_app/ai-consent")({
  validateSearch: (s: Record<string, unknown>): ConsentSearch => ({
    flow: typeof s.flow === "string" ? s.flow : undefined,
  }),
  component: AiConsentPage,
});

function AiConsentPage() {
  const { flow } = Route.useSearch();
  const [view, setView] = useState<FlowView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Step-up MFA state.
  const [mfa, setMfa] = useState<StepUpStart | null>(null);
  const [factorId, setFactorId] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [mfaErr, setMfaErr] = useState<string | null>(null);
  const [stepUpToken, setStepUpToken] = useState<string | null>(null);

  useEffect(() => {
    if (!flow) {
      setErr("Missing authorization request.");
      return;
    }
    api<FlowView>(`/admin/ai/oauth/flows/${encodeURIComponent(flow)}`)
      .then((v) => {
        setView(v);
        // Kick off the step-up challenge as soon as we know the flow is real.
        return api<StepUpStart>("/admin/ai/oauth/mfa/start", { method: "POST" });
      })
      .then((s) => {
        if (!s) return;
        setMfa(s);
        if (s.factors && s.factors.length > 0) setFactorId(s.factors[0].factor_id);
      })
      .catch((e) =>
        setErr(e instanceof APIError ? e.message : "Failed to load the request."),
      );
  }, [flow]);

  const verifyMfa = async () => {
    if (!mfa?.challenge_id || !factorId || !code) return;
    setVerifying(true);
    setMfaErr(null);
    try {
      const r = await api<{ stepup_token: string }>("/admin/ai/oauth/mfa/verify", {
        method: "POST",
        body: { challenge_id: mfa.challenge_id, factor_id: factorId, code },
      });
      setStepUpToken(r.stepup_token);
    } catch (e) {
      setMfaErr(e instanceof APIError ? e.message : "Verification failed.");
    } finally {
      setVerifying(false);
    }
  };

  const decide = async (approve: boolean) => {
    if (!flow) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ redirect_to: string }>("/admin/ai/oauth/consent", {
        method: "POST",
        body: { flow_id: flow, approve, stepup_token: stepUpToken ?? undefined },
      });
      // Hand the browser back to the AI client's redirect_uri with the code.
      window.location.assign(r.redirect_to);
    } catch (e) {
      setErr(e instanceof APIError ? e.message : "Something went wrong.");
      setBusy(false);
    }
  };

  const mfaRequired = mfa?.mfa_required === true;
  const mfaSatisfied = !mfaRequired || stepUpToken !== null;
  const selectedKind = mfa?.factors?.find((f) => f.factor_id === factorId)?.kind;

  return (
    <div className="mx-auto max-w-xl py-10">
      <Card>
        <CardHeader>
          <CardTitle>Connect an AI assistant</CardTitle>
        </CardHeader>
        <CardBody className="space-y-5">
          {err && (
            <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {err}
            </div>
          )}
          {!view && !err && <div className="text-sm text-muted">Loading request…</div>}
          {view && (
            <>
              <p className="text-sm text-t-2">
                <span className="font-semibold text-t-1">{view.client_name}</span>{" "}
                <span className="font-mono text-xs text-muted">({view.redirect_host})</span>{" "}
                is requesting access to the Smart Market admin tools{" "}
                <span className="font-semibold">on your behalf</span>. It will act strictly
                under <span className="font-semibold">your own permissions</span>.
              </p>

              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-t-3">
                  Requested access
                </div>
                <ul className="space-y-2">
                  {view.scopes.map((s) => (
                    <li key={s} className="flex items-start gap-2 text-sm">
                      <Badge tone="muted">{s}</Badge>
                      <span className="text-t-2">{SCOPE_LABELS[s] ?? s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-xs text-muted">
                Creating events or markets produces <span className="font-semibold">drafts only</span>{" "}
                — nothing becomes visible to users until a human publishes it. You can revoke
                this connection at any time.
              </p>

              {mfaRequired && (
                <div className="space-y-3 rounded-md border border-line bg-surface-2 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-t-3">
                    Confirm it's you
                  </div>
                  {stepUpToken ? (
                    <div className="flex items-center gap-2 text-sm text-emerald-400">
                      <span aria-hidden>✓</span> Identity verified — you can grant access.
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-muted">
                        Enter a code from your second factor to authorize this connection.
                      </p>
                      {(mfa?.factors?.length ?? 0) > 1 && (
                        <Select
                          value={factorId}
                          onChange={(e) => setFactorId(e.target.value)}
                          options={(mfa?.factors ?? []).map((f) => ({
                            value: f.factor_id,
                            label:
                              (FACTOR_KIND_LABEL[f.kind] ?? f.kind) +
                              (f.friendly_name ? ` — ${f.friendly_name}` : ""),
                          }))}
                        />
                      )}
                      {mfaErr && (
                        <div className="text-sm text-red-400">{mfaErr}</div>
                      )}
                      <div className="flex gap-2">
                        <Input
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder="123456"
                          className="max-w-[8rem] font-mono tracking-widest"
                        />
                        <Button
                          variant="secondary"
                          onClick={verifyMfa}
                          disabled={verifying || code.length < 6}
                        >
                          {verifying ? "Verifying…" : "Verify"}
                        </Button>
                      </div>
                      {selectedKind === "EMAIL_OTP" && (
                        <p className="text-xs text-muted">
                          We emailed you a one-time code.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => decide(true)}
                  disabled={busy || view.status !== "pending" || !mfaSatisfied}
                >
                  {busy ? "Granting…" : "Grant access"}
                </Button>
                <Button variant="ghost" onClick={() => decide(false)} disabled={busy}>
                  Deny
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
